'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { fetchPublishedGames, recordGamePlayApi } from '@/lib/api';
import {
  GamepadIcon,
  ZapIcon,
  PlayIcon,
  RocketIcon,
} from '@/components/Icons';
import ArcadeBetaPlayer, { BETA_GAMES, BetaGameDef } from '@/components/ArcadeBetaGames';
import SEOHead from '@/components/SEOHead';

const GENRES = [
  { id: 'ALL', label: 'All Games' },
  { id: 'FOOTBALL', label: 'Football' },
  { id: 'CRICKET', label: 'Cricket' },
  { id: 'BASKETBALL', label: 'Basketball' },
  { id: 'ARCADE', label: 'Arcade' },
  { id: 'CASUAL', label: 'Casual' },
];

export default function GamesArcadePage() {
  const [communityGames, setCommunityGames] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [activePlayGame, setActivePlayGame] = useState<any | null>(null);

  useEffect(() => {
    async function loadGames() {
      setLoading(true);
      try {
        const fetched = await fetchPublishedGames(selectedGenre, searchQuery);
        setCommunityGames(fetched || []);
      } catch (err) {
        console.warn('Failed to load published games:', err);
      } finally {
        setLoading(false);
      }
    }

    loadGames();
  }, [selectedGenre, searchQuery]);

  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      if (!e.data || typeof e.data !== 'object') return;
      if (e.data.type === 'KHELDROP_SCORE') {
        const score = Number(e.data.score) || 0;
        console.log('[KHELDROP RUNTIME] Score recorded from sandboxed game:', score);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleLaunchGame = (game: any) => {
    if (game.isBetaGame) {
      setActivePlayGame(game);
      return;
    }

    const gamesOrigin = process.env.NEXT_PUBLIC_GAMES_ORIGIN || 'https://games.kheldrop.workers.dev';
    let bundleUrl = game.activeVersionId?.bundleUrl;
    if (game.r2Key) {
      bundleUrl = `${gamesOrigin}/${game.r2Key.replace(/^games\//, '')}`;
    }
    setActivePlayGame({ ...game, resolvedBundleUrl: bundleUrl });
    recordGamePlayApi(game.id || game._id);
  };

  // Combine Beta games with any community published games
  const filteredBetaGames = BETA_GAMES.filter(g => {
    const matchGenre = selectedGenre === 'ALL' || g.genre === selectedGenre;
    const matchSearch = !searchQuery.trim() ||
      g.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchGenre && matchSearch;
  });

  const filteredCommunity = communityGames.filter(g => {
    const matchGenre = selectedGenre === 'ALL' || (g.genre && g.genre.toUpperCase() === selectedGenre);
    const matchSearch = !searchQuery.trim() ||
      (g.title && g.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (g.description && g.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchGenre && matchSearch;
  });

  const allDisplayGames = [...filteredBetaGames, ...filteredCommunity];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg, #06070A)',
      color: 'var(--text, #F5F7F6)',
      fontFamily: 'var(--font-body, Inter, sans-serif)',
    }}>
      <SEOHead
        title="Arcade Games"
        description="Play instant 2D and 3D browser games, football penalties, cricket strike, and community tournaments on KhelDrop with zero entry fees."
        canonical="/games"
        keywords="kheldrop games, browser arcade, sports web games, football penalty, cricket strike, free web games"
      />
      {/* Top Header */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 28px',
        borderBottom: '1px solid var(--border, #1E2230)',
        background: 'var(--surface, #0F1117)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Link href="/" style={{
            color: 'var(--text-secondary, #8B92A5)',
            textDecoration: 'none',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Back to Matches
          </Link>
          <div style={{ width: '1px', height: '18px', background: 'var(--border, #1E2230)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <GamepadIcon size={20} className="text-violet-400" />
            <span style={{
              fontFamily: 'var(--font-display, Space Grotesk, sans-serif)',
              fontSize: '18px',
              fontWeight: 800,
              letterSpacing: '-0.02em',
            }}>
              KhelDrop Cloud Arcade
            </span>
            <span style={{
              background: '#7C3AED',
              color: '#FFFFFF',
              fontSize: '10px',
              fontWeight: 800,
              padding: '2px 8px',
              borderRadius: '6px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}>
              BETA v0.9
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link
            href="/creator"
            style={{
              background: 'var(--elevated, #161922)',
              border: '1px solid var(--border, #1E2230)',
              color: 'var(--text, #FFFFFF)',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RocketIcon size={14} className="text-violet-400" />
            <span>Creator Studio</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: '36px 24px' }}>
        {/* Banner */}
        <div style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #18112d 0%, #10121d 60%, #0a0b12 100%)',
          border: '1px solid rgba(139, 92, 246, 0.4)',
          borderRadius: '16px',
          padding: '28px 32px',
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
          boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.06) inset',
          overflow: 'hidden',
        }}>
          {/* Subtle background glow effect */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            left: '-50px',
            width: '240px',
            height: '240px',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div style={{ position: 'relative', zIndex: 1, maxWidth: '640px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{
                background: 'rgba(139, 92, 246, 0.28)',
                border: '1px solid rgba(167, 139, 250, 0.5)',
                color: '#E9D5FF',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.06em',
                padding: '3px 9px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
              }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#A855F7', display: 'inline-block' }} />
                PUBLIC BETA
              </span>
              <span style={{ fontSize: '13px', color: '#CBD5E1', fontWeight: 500, letterSpacing: '0.01em' }}>
                WebGL &amp; HTML5 Runtime v0.9
              </span>
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display, Space Grotesk, sans-serif)',
              fontSize: '28px',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
              margin: '0 0 8px',
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.6)',
            }}>
              Instant Sports Arcade
            </h1>
            <p style={{
              color: '#E2E8F0',
              fontSize: '14.5px',
              lineHeight: 1.6,
              margin: 0,
              fontWeight: 400,
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
            }}>
              Play built-in beta arcade sports mini-games right in your browser, or build and deploy your own games with the KhelDrop Creator Studio SDK.
            </p>
          </div>

          <Link
            href="/creator"
            style={{
              position: 'relative',
              zIndex: 1,
              background: '#FFFFFF',
              color: '#0F172A',
              padding: '11px 22px',
              borderRadius: '10px',
              fontSize: '13.5px',
              fontWeight: 700,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
          >
            <RocketIcon size={15} />
            <span>Publish Your Game</span>
          </Link>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '28px' }}>
          <div style={{
            display: 'flex',
            gap: '6px',
            overflowX: 'auto',
            paddingBottom: '4px',
            flex: '1 1 500px',
          }}>
            {GENRES.map(g => (
              <button
                key={g.id}
                type="button"
                onClick={() => setSelectedGenre(g.id)}
                style={{
                  background: selectedGenre === g.id ? '#FFFFFF' : 'var(--surface, #0F1117)',
                  color: selectedGenre === g.id ? '#000000' : 'var(--text-secondary, #8B92A5)',
                  border: `1px solid ${selectedGenre === g.id ? '#FFFFFF' : 'var(--border, #1E2230)'}`,
                  padding: '7px 14px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: selectedGenre === g.id ? 700 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                {g.label}
              </button>
            ))}
          </div>

          <div style={{ flex: '1 1 240px', maxWidth: '300px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search arcade titles, tags..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 14px',
                background: 'var(--surface, #0F1117)',
                border: '1px solid var(--border, #1E2230)',
                borderRadius: '10px',
                color: 'var(--text, #FFFFFF)',
                fontSize: '13px',
                outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Games Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
            <div className="auth-spinner" style={{ width: '28px', height: '28px', margin: '0 auto 16px', display: 'block' }} />
            <p style={{ fontSize: '14px' }}>Loading games…</p>
          </div>
        ) : allDisplayGames.length === 0 ? (
          <div style={{
            background: 'var(--surface, #0F1117)',
            border: '1px dashed var(--border, #1E2230)',
            borderRadius: '16px',
            padding: '54px 24px',
            textAlign: 'center',
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <GamepadIcon size={40} className="text-zinc-500" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px' }}>No games found in this genre</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '420px', margin: '0 auto 20px' }}>
              Try selecting another category or publish a game from Creator Studio.
            </p>
            <Link
              href="/creator"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: '#FFFFFF',
                color: '#000000',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
              }}
            >
              <RocketIcon size={15} />
              <span>Publish in Creator Studio</span>
            </Link>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
          }}>
            {allDisplayGames.map((game: any) => (
              <div
                key={game._id || game.id}
                style={{
                  background: 'var(--surface, #0F1117)',
                  border: '1px solid var(--border, #1E2230)',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, border-color 0.2s',
                }}
              >
                {/* Game Card Header Image / Banner */}
                <div style={{
                  height: '140px',
                  background: game.color ? `linear-gradient(135deg, ${game.color}22, #0F1117)` : 'linear-gradient(135deg, #161922, #0F1117)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  borderBottom: '1px solid var(--border, #1E2230)',
                }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    background: game.color ? `${game.color}33` : 'rgba(124, 58, 237, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: game.color || '#A78BFA',
                  }}>
                    <GamepadIcon size={30} />
                  </div>

                  <span style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: '6px',
                    background: 'rgba(0, 0, 0, 0.65)',
                    color: '#FFFFFF',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}>
                    {game.version || `v${game.currentVersion || '1.0'}`}
                  </span>

                  <span style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    fontSize: '10px',
                    fontWeight: 800,
                    padding: '2px 7px',
                    borderRadius: '4px',
                    background: '#7C3AED',
                    color: '#FFFFFF',
                    letterSpacing: '0.04em',
                  }}>
                    BETA
                  </span>
                </div>

                {/* Game Info */}
                <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '17px', fontWeight: 700, margin: 0 }}>
                      {game.title}
                    </h3>
                  </div>

                  <p style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary, #8B92A5)',
                    lineHeight: 1.5,
                    margin: '0 0 16px',
                    flex: 1,
                  }}>
                    {game.description || 'Interactive sports arcade mini-game on KhelDrop.'}
                  </p>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border, #1E2230)',
                    marginBottom: '16px',
                    fontSize: '12px',
                    color: 'var(--text-muted, #4B5267)',
                  }}>
                    <span>{game.isBetaGame ? 'KhelDrop Studios' : (game.creatorId?.name || 'Community')}</span>
                    <span>{game.playsCount || 0} plays</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleLaunchGame(game)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#FFFFFF',
                      color: '#000000',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <PlayIcon size={14} />
                    <span>Play Beta Now</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Fullscreen Player Modal */}
      {activePlayGame && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.92)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {activePlayGame.isBetaGame ? (
            <ArcadeBetaPlayer
              game={activePlayGame}
              onClose={() => setActivePlayGame(null)}
            />
          ) : (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 24px',
                background: 'var(--surface, #0F1117)',
                borderBottom: '1px solid var(--border, #1E2230)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '16px', fontWeight: 700 }}>{activePlayGame.title}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    v{activePlayGame.currentVersion || '1.0.0'}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'rgba(124, 58, 237, 0.12)',
                    color: 'var(--primary, #7C3AED)',
                    border: '1px solid rgba(124, 58, 237, 0.3)',
                  }}>
                    Sandboxed Runtime
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setActivePlayGame(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text)',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>

              <div style={{ flex: 1, position: 'relative', background: '#000' }}>
                {(activePlayGame.resolvedBundleUrl || activePlayGame.activeVersionId?.bundleUrl) ? (
                  <iframe
                    src={activePlayGame.resolvedBundleUrl || activePlayGame.activeVersionId?.bundleUrl}
                    title={activePlayGame.title}
                    sandbox="allow-scripts"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                  />
                ) : (
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: 'var(--text-secondary)',
                  }}>
                    <ZapIcon size={40} className="text-violet-400 mb-4" />
                    <h3 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 8px', color: 'var(--text)' }}>
                      Interactive Simulator Ready
                    </h3>
                    <p style={{ maxWidth: '440px', textAlign: 'center', fontSize: '14px', lineHeight: 1.6 }}>
                      This game is configured for the KhelDrop Client Runtime SDK.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
